import React from 'react';
// import PropTypes from 'prop-types';

import { PageContext } from '../../../contexts/ContextCreator';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import VisibilitySensor from 'react-visibility-sensor';
import useWindowScroll from '../../../utilities/hooks/useWindowScroll';
import usePrevious from '../../../utilities/hooks/usePrevious';
import { CSSTransition } from 'react-transition-group';

import './ScrollTopButton.scss';

const handleScroll = () =>
    window && window.scrollTo({ top: 0, behavior: 'smooth' });

const handleMetric = (breakpoint) => {
    const isDesktop = breakpoint >= 3;

    return EmitMetric({
        misc_event_count: 1,
        misc_event_type: isDesktop
            ? 'jobresults-backtotop-click'
            : 'mobile-jobresults-backtotop-click',
    });
};

export const ScrollTopButton = (props) => {
    const { responsive, pageProps, modalActive } = React.useContext(
        PageContext
    );

    const { breakpoint } = responsive;
    const {
        featureFlags: { jobsBackToTop },
    } = pageProps;

    const handleClick = React.useCallback(() => {
        handleMetric(breakpoint);

        return handleScroll();
    }, [breakpoint]);

    if (!jobsBackToTop || modalActive) return null;

    return (
        <button
            type="button"
            onClick={handleClick}
            className="component-Elements-ScrollTopButton"
        >
            <span className="button-text desktop">Back to top</span>
            <span className="button-text mobile">Top</span>
            <span className="button-icon">
                <span className="arrow-icon">&#10140;</span>
            </span>
        </button>
    );
};

ScrollTopButton.propTypes = {};

ScrollTopButton.defaultProps = {};

ScrollTopButton.displayName = 'ScrollTopButton';

export default React.memo(ScrollTopButton);

// offset top/bottom boundaries up 80px to trigger before visible
const sensorOfst = { top: -80, bottom: -80 };

// Utility component that will hide/unhide (w fancy transitions!) the scroll top button
// Use by wrapping a component (hidden from viewport) with this one
// - Scrolling to/below the wrapped component shows the scroll top button
// - Scrolling above hides it
export const ScrollVisWrapper = React.memo(({ children }) => {
    const [visible, setVisibility] = React.useState(false);
    const prevVisible = usePrevious(visible);

    const [scrollButtonVisible, setScrollButtonVisible] = React.useState(false);
    const [triggerButtonHeight, setTriggerButtonHeight] = React.useState(null);

    // how far user has scrolled down
    const { y = 0 } = useWindowScroll();

    const handleChange = React.useCallback(
        (isVisible) => setVisibility(isVisible),
        [setVisibility]
    );

    React.useEffect(() => {
        if (typeof y !== 'number') return;

        if (visible && !prevVisible && triggerButtonHeight === null) {
            setTriggerButtonHeight(y);
        }
    }, [visible, prevVisible, y, triggerButtonHeight, setTriggerButtonHeight]);

    React.useEffect(() => {
        if (!triggerButtonHeight || typeof y !== 'number') return;

        const isBelowTriggerHeight = y >= triggerButtonHeight;

        setScrollButtonVisible(isBelowTriggerHeight);
    }, [y, triggerButtonHeight]);

    return (
        <React.Fragment>
            <VisibilitySensor
                onChange={handleChange}
                offset={sensorOfst}
                scrollCheck
                resizeCheck
                partialVisibility
                delayedCall
            >
                {children}
            </VisibilitySensor>
            <CSSTransition
                classNames="component-Elements-ScrollTopButton"
                in={scrollButtonVisible}
                timeout={350}
                mountOnEnter
                unmountOnExit
            >
                <ScrollTopButton />
            </CSSTransition>
        </React.Fragment>
    );
});
